import React, { useMemo, useCallback } from 'react';
import cx from 'classnames';
import { Grid } from '@visx/grid';
import { DateAxisBottom } from 'js/components/Charts/Elements/DateAxisBottom';
import { YearUnderlay } from 'js/components/Charts/Elements/YearUnderlay';
import { curveMonotoneX } from '@visx/curve';
import ElementTrendLine from './ElementTrendLine';
import AreaPath from 'js/components/Charts/Elements/AreaPath';
import styles from './CapabilityTrendsChart.module.scss';
import LineWithRings from 'js/components/Charts/Elements/LineWithRings';
import NoActivity from './NoActivity';

function getHighlightedData(data, filter) {
  if (filter.type === 'capability') {
    return data.find((d) => d.capabilityId === filter.criteria.capabilityId);
  }
}

export function ElementTrends({
  width,
  height,
  data,
  margin,
  paletteMap,
  scaleX,
  scaleY,
  filter,
  onHoverTrend = () => {},
  onUnhoverTrend = () => {},
  onClickTrend = () => {},
}) {
  margin = { top: 10, right: 15, bottom: 15, left: 15, ...margin };
  const chartHeight = height - margin.top - margin.bottom;
  const chartWidth = width - margin.left - margin.right;

  const strippedData = useMemo(
    () =>
      Object.entries(data).map(([capabilityId, points]) => {
        return {
          capabilityId,
          // A score of 0 (every response at the bottom of the scale) is data
          dataPoints: points.filter((d) => d?.data?.normalizedScore != null),
        };
      }),
    [data]
  );

  const highlightedData = useMemo(
    () => (filter === null ? null : getHighlightedData(strippedData, filter)),
    [filter, strippedData]
  );

  const x = useCallback(
    (d) => scaleX(d?.userGroupAssessment?.went_live_at),
    [scaleX]
  );
  const y = useCallback((d) => scaleY(d?.data?.normalizedScore), [scaleY]);
  const yUpper = useCallback((d) => scaleY(d?.data?.maxScore), [scaleY]);
  const yLower = useCallback((d) => scaleY(d?.data?.minScore), [scaleY]);

  const handleHoverHighlightedData = useCallback(() => {
    onHoverTrend(highlightedData.capabilityId);
  }, [onHoverTrend, highlightedData]);

  const handleClickHighlightedData = useCallback(
    () => onClickTrend(highlightedData.capabilityId),
    [onClickTrend, highlightedData]
  );

  const hasData = useMemo(() => {
    return strippedData.some(({ dataPoints }) => dataPoints.length > 0);
  }, [strippedData]);

  return hasData ? (
    <div className={styles['element-trends-chart']}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <YearUnderlay height={chartHeight} xScale={scaleX} />
          <Grid
            className="chart-grid chart-neutral-line"
            height={chartHeight}
            width={chartWidth}
            rowTickValues={[0, 0.5, 1]}
            xScale={scaleX}
            yScale={scaleY}
            stroke="#526067"
            numTicksColumns={5}
          />
          <DateAxisBottom
            chartHeight={chartHeight}
            chartWidth={chartWidth}
            xScale={scaleX}
          />
          <g>
            {strippedData.map(({ capabilityId, dataPoints }) =>
              highlightedData?.capabilityId === capabilityId ? null : (
                <ElementTrendLine
                  key={capabilityId}
                  capabilityId={capabilityId}
                  className={`line ${paletteMap[capabilityId]?.className} palette-muted`}
                  data={dataPoints}
                  x={x}
                  y={y}
                  onMouseOver={onHoverTrend}
                  onMouseOut={onUnhoverTrend}
                  onClick={onClickTrend}
                />
              )
            )}
          </g>
          <rect
            width={chartWidth}
            height={chartHeight}
            className="bounding-box"
          />
          {highlightedData && (
            <g>
              <g
                className={`${
                  paletteMap[highlightedData.capabilityId]?.className
                }`}
              >
                <AreaPath
                  data={highlightedData.dataPoints}
                  className={cx('palette-fill', styles['area-path'])}
                  x={x}
                  yUpper={yUpper}
                  yLower={yLower}
                  curve={curveMonotoneX}
                />
              </g>
              <g
                className={`line ${
                  paletteMap[highlightedData.capabilityId]?.className
                }`}
              >
                <LineWithRings
                  data={highlightedData.dataPoints}
                  x={x}
                  y={y}
                  onMouseOut={onUnhoverTrend}
                  onMouseOver={handleHoverHighlightedData}
                  onClick={handleClickHighlightedData}
                />
              </g>
            </g>
          )}
        </g>
      </svg>
    </div>
  ) : (
    <NoActivity
      width={width}
      height={height}
      margin={margin}
      scaleX={scaleX}
      scaleY={scaleY}
    />
  );
}

export default ElementTrends;
